import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Camera, HelpCircle, Image as ImageIcon, MessageSquare, X } from "lucide-react";
import { createForumPost, listForumCategories } from "@/lib/forum.functions";
import { setPostTags } from "@/lib/forum-extra.functions";
import { TagPicker } from "@/components/forum/TagPicker";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";

export const Route = createFileRoute("/_authenticated/forum/nytt")({
  head: () => ({
    meta: [
      { title: "Nytt inlägg – Fritidsforum | AK Husbil & Fritid" },
      { name: "description", content: "Skapa ett nytt inlägg i Fritidsforum: ställ en fråga, starta en diskussion eller dela bilder från din resa." },
      { property: "og:title", content: "Nytt inlägg – Fritidsforum" },
      { property: "og:description", content: "Ställ en fråga, starta en diskussion eller dela din resa i communityn." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: NewPost,
});

const TYPES = [
  { key: "question", label: "Fråga", icon: HelpCircle, hint: "Du får markera bästa svar" },
  { key: "discussion", label: "Diskussion", icon: MessageSquare, hint: "Öppet samtal" },
  { key: "trip", label: "Bild & resa", icon: ImageIcon, hint: "Dela bilder från vägen" },
] as const;

function NewPost() {
  const cats = useServerFn(listForumCategories);
  const create = useServerFn(createForumPost);
  const saveTags = useServerFn(setPostTags);
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data } = useQuery({ queryKey: ["forum", "categories"], queryFn: () => cats() });

  const [categoryId, setCategoryId] = useState("");
  const [postType, setPostType] = useState<"question" | "discussion" | "trip">("question");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [paths, setPaths] = useState<string[]>([]);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const categories = data?.categories ?? [];
  const activeCategory = categoryId || categories[0]?.id || "";

  async function onFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (!files.length || !user) return;
    setUploading(true);
    try {
      const next: string[] = [];
      for (const file of files.slice(0, 8 - paths.length)) {
        const ext = file.name.split(".").pop() ?? "jpg";
        const path = `forum/${user.id}/${crypto.randomUUID()}.${ext}`;
        const { error } = await supabase.storage
          .from("user-content")
          .upload(path, file, { contentType: file.type });
        if (error) throw new Error(error.message);
        next.push(path);
      }
      setPaths((prev) => [...prev, ...next]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunde inte ladda upp bild");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!activeCategory) return toast.error("Välj en kategori");
    if (title.trim().length < 6) return toast.error("Rubriken behöver vara minst 6 tecken");
    if (body.trim().length < 10) return toast.error("Skriv lite mer i texten");
    setSaving(true);
    try {
      const res = await create({
        data: {
          category_id: activeCategory,
          title: title.trim(),
          body: body.trim(),
          post_type: postType,
          image_paths: paths,
        },
      });
      if (tagIds.length && res.post_id) {
        await saveTags({ data: { post_id: res.post_id, tag_ids: tagIds } }).catch(() => null);
      }
      toast.success("Inlägget är publicerat");
      navigate({ to: "/forum/t/$slug", params: { slug: res.slug } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunde inte publicera");
    } finally {
      setSaving(false);
    }
  }

  const inputClass =
    "w-full rounded-2xl border border-input bg-background px-4 py-3 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20";

  return (
    <section className="bg-warm">
      <div className="container-edge py-10 md:py-16">
        <Link to="/forum" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={15} /> Fritidsforum
        </Link>

        <h1 className="mt-6 font-display text-4xl leading-tight md:text-5xl">Nytt inlägg</h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base">
          Skriv tydligt och konkret — beskriv modell, årsmodell och vad du redan testat. Då får du
          snabbast bra svar från andra ägare.
        </p>

        <form onSubmit={submit} className="mt-8 max-w-3xl space-y-6">
          <div>
            <label className="text-sm font-semibold">Typ av inlägg</label>
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setPostType(t.key)}
                  className={`rounded-2xl border p-4 text-left transition ${
                    postType === t.key
                      ? "border-accent bg-accent/10"
                      : "border-border bg-card hover:bg-secondary"
                  }`}
                >
                  <span className="inline-flex items-center gap-2 text-sm font-semibold">
                    <t.icon size={15} /> {t.label}
                  </span>
                  <span className="mt-1 block text-xs text-muted-foreground">{t.hint}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold">Taggar</label>
            <div className="mt-3 rounded-2xl border border-border bg-card p-4">
              <TagPicker selected={tagIds} onChange={setTagIds} />
            </div>
          </div>

          <div>
            <label htmlFor="cat" className="text-sm font-semibold">Kategori</label>
            <select
              id="cat"
              value={activeCategory}
              onChange={(e) => setCategoryId(e.target.value)}
              className={`${inputClass} mt-2`}
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="title" className="text-sm font-semibold">Rubrik</label>
            <input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={160}
              placeholder="T.ex. Truma värmare startar inte i kyla – Rapido 866F 2019"
              className={`${inputClass} mt-2`}
            />
          </div>

          <div>
            <label htmlFor="body" className="text-sm font-semibold">Text</label>
            <textarea
              id="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              maxLength={20000}
              placeholder="Beskriv situationen: fordon, årsmodell, vad som händer och vad du testat."
              className={`${inputClass} mt-2`}
            />
          </div>

          <div>
            <label className="text-sm font-semibold">Bilder (frivilligt, max 8)</label>
            <p className="mt-1 text-xs text-muted-foreground">
              Bild på felet eller platsen gör att andra kan hjälpa dig mycket snabbare.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {paths.map((p) => (
                <span key={p} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs">
                  Bild uppladdad
                  <button type="button" onClick={() => setPaths((prev) => prev.filter((x) => x !== p))} aria-label="Ta bort bild">
                    <X size={12} />
                  </button>
                </span>
              ))}
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm hover:bg-secondary">
                <Camera size={15} /> {uploading ? "Laddar…" : "Lägg till bild"}
                <input type="file" accept="image/*" multiple onChange={onFiles} className="hidden" />
              </label>
            </div>
          </div>

          <button
            disabled={saving || uploading}
            className="rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-60"
          >
            {saving ? "Publicerar…" : "Publicera inlägg"}
          </button>
        </form>
      </div>
    </section>
  );
}
